/**
 * The branding editor (§A PR-4) — routes P11/P12, plus the asset slots.
 *
 * Three properties this screen exists to hold:
 *
 * 1. **The PUT carries only what changed.** A save that echoes all ten fields
 *    would claim authorship of colours the user never touched and re-trip
 *    contrast validation on them. Dirtiness is computed against the payload
 *    the server sent, field by field.
 * 2. **The server's judgement renders verbatim.** A `CONTRAST_TOO_LOW` reject
 *    names the achieved ratio ("…would reach only 2.31:1…"), and that message
 *    binds to the field that caused it via `ApiClientError.fieldErrors()` —
 *    the panel does not re-derive or paraphrase a measurement the server
 *    already made. Warnings (3.0–4.5:1) come back in `payload.warnings` and
 *    are shown, not swallowed: a marginal colour is the tenant's call, but
 *    only if they are told.
 * 3. **A successful save restyles the app live**, through the same
 *    `adoptBranding` call `BrandingProvider` makes — one writer of the custom
 *    properties (§11.5), so the editor cannot drift from the boot path.
 *
 * The semantic colours (danger/warning/success) are **absent by design**, and
 * the page says so: §11.6 makes them platform-defined so a tenant cannot make
 * "Disconnect" look like "Save", and the API's strict schema rejects them by
 * name.
 */

import { ALLOWED_FONT_FAMILIES, type BrandingPayload, type RadiusScale } from '@rd/shared';
import { useState, type ReactElement } from 'react';

import { useAuth } from '../../auth/AuthProvider.js';
import { PERMISSION } from '../../auth/keys.js';
import { useBranding } from '../../branding/BrandingProvider.js';
import { Button } from '../../components/Button.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Input, Select } from '../../components/Input.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Skeleton } from '../../components/Skeleton.js';
import { ApiClientError } from '../../lib/apiClient.js';

import { AssetUploader } from './AssetUploader.js';
import { useBrandingMe, useUpdateBranding, type UpdateBrandingBody } from './useBrandingAdmin.js';

/** The six editable colours: PUT body key ↔ payload key ↔ label. */
const COLOR_FIELDS = [
  { body: 'colorPrimary', payload: 'primary', label: 'Primary' },
  { body: 'colorAccent', payload: 'accent', label: 'Accent' },
  { body: 'colorSurface', payload: 'surface', label: 'Surface' },
  { body: 'colorSurfaceAlt', payload: 'surfaceAlt', label: 'Alternate surface' },
  { body: 'colorText', payload: 'text', label: 'Body text' },
  { body: 'colorTextMuted', payload: 'textMuted', label: 'Muted text' },
] as const;

type ColorBodyKey = (typeof COLOR_FIELDS)[number]['body'];

const RADIUS_OPTIONS: readonly RadiusScale[] = ['none', 'sm', 'md', 'lg'];

/** Six bare hex digits, as the API stores them — never a leading `#`. */
function isBareHex(value: string): boolean {
  return /^[0-9A-Fa-f]{6}$/.test(value);
}

/** The payload's `#RRGGBB` as the bare form the inputs edit. */
function bareHexOf(
  payload: BrandingPayload,
  key: (typeof COLOR_FIELDS)[number]['payload'],
): string {
  return payload.colors[key].hex.replace(/^#/, '').toUpperCase();
}

export function BrandingPage(): ReactElement {
  const { hasPermission } = useAuth();
  const query = useBrandingMe();
  const canWrite = hasPermission(PERMISSION.brandingWrite);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  return (
    <>
      <PageHeader
        title="Branding"
        description="How this panel looks to everyone in your account — colours, type, logos."
      />

      {query.isPending ? (
        <div className="flex flex-col gap-3 lg:max-w-3xl">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data ? (
        <div className="flex flex-col gap-6 lg:max-w-3xl">
          {savedVersion !== null && savedVersion === query.data.version ? (
            <p role="status" className="text-[length:var(--text-sm)] text-muted">
              Branding saved and applied.
            </p>
          ) : null}

          {/*
            Keyed on `version` so a landed save (or another admin's edit
            arriving on refetch) resets the form to the new authoritative
            baseline — dirtiness is always measured against what is stored.
          */}
          <BrandingForm
            key={query.data.version}
            payload={query.data}
            canWrite={canWrite}
            onSaved={(payload) => setSavedVersion(payload.version)}
          />

          <section aria-labelledby="branding-assets-heading">
            <h2
              id="branding-assets-heading"
              className="mb-3 text-[length:var(--text-lg)] font-medium text-body"
            >
              Logos and images
            </h2>
            <AssetUploader payload={query.data} canWrite={canWrite} />
          </section>
        </div>
      ) : null}
    </>
  );
}

function BrandingForm({
  payload,
  canWrite,
  onSaved,
}: {
  payload: BrandingPayload;
  canWrite: boolean;
  onSaved(payload: BrandingPayload): void;
}): ReactElement {
  const { adoptBranding } = useBranding();
  const update = useUpdateBranding();

  const [colors, setColors] = useState<Record<ColorBodyKey, string>>(() => {
    const out = {} as Record<ColorBodyKey, string>;
    for (const field of COLOR_FIELDS) out[field.body] = bareHexOf(payload, field.payload);
    return out;
  });
  const [productName, setProductName] = useState(payload.productName);
  const [radiusScale, setRadiusScale] = useState<RadiusScale>(payload.radiusScale);
  const [fontFamily, setFontFamily] = useState(payload.fontFamily);
  const [supportEmail, setSupportEmail] = useState(payload.supportEmail ?? '');
  const [footerText, setFooterText] = useState(payload.footerText ?? '');

  /** Only what differs from the stored payload — the whole point of the PUT. */
  const dirtyBody = (): UpdateBrandingBody => {
    const body: UpdateBrandingBody = {};
    for (const field of COLOR_FIELDS) {
      const current = colors[field.body].trim();
      // Compared uppercase-to-uppercase, because the server stores uppercase
      // and `ff6600` retyped over `FF6600` is not a change.
      if (current.toUpperCase() !== bareHexOf(payload, field.payload)) {
        body[field.body] = current;
      }
    }
    if (productName.trim() !== payload.productName) body.productName = productName.trim();
    if (radiusScale !== payload.radiusScale) body.radiusScale = radiusScale;
    if (fontFamily !== payload.fontFamily) {
      // The select's options come from the same list the server validates
      // against, so the narrowing here cannot fail at runtime.
      body.fontFamily = fontFamily as NonNullable<UpdateBrandingBody['fontFamily']>;
    }
    if (supportEmail.trim() !== (payload.supportEmail ?? '')) {
      body.supportEmail = supportEmail.trim() === '' ? null : supportEmail.trim();
    }
    if (footerText.trim() !== (payload.footerText ?? '')) {
      body.footerText = footerText.trim() === '' ? null : footerText.trim();
    }
    return body;
  };

  const isDirty = Object.keys(dirtyBody()).length > 0;

  const fieldErrors = update.error instanceof ApiClientError ? update.error.fieldErrors() : {};

  const save = async (): Promise<void> => {
    try {
      const saved = await update.mutateAsync(dirtyBody());
      // The same call BrandingProvider makes: one writer of the custom
      // properties, so "saved" and "applied" cannot be two different palettes.
      adoptBranding(saved);
      onSaved(saved);
    } catch {
      // `update.error` renders below — per field where the server named one,
      // as a summary otherwise.
    }
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
        <h2 className="text-[length:var(--text-lg)] font-medium text-body">Colours</h2>
        <p className="mt-1 text-[length:var(--text-sm)] text-muted">
          Six hexadecimal digits, without a leading #.
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {COLOR_FIELDS.map((field) => (
            <Field key={field.body} label={field.label} error={fieldErrors[field.body]}>
              {(props) => (
                <div className="flex items-center gap-2">
                  <Input
                    {...props}
                    value={colors[field.body]}
                    disabled={!canWrite}
                    maxLength={6}
                    className="font-[family-name:var(--font-mono)]"
                    onChange={(e) =>
                      setColors((current) => ({ ...current, [field.body]: e.target.value }))
                    }
                  />
                  {/*
                    A live swatch of the typed value. The colour is computed at
                    runtime from tenant-entered state — the house rule outlaws
                    colour *literals* in source, and there is none here; an
                    invalid draft renders no swatch rather than a guess.
                  */}
                  <span
                    aria-hidden="true"
                    data-testid={`swatch-${field.body}`}
                    className="size-9 shrink-0 rounded-[var(--radius-sm)] border border-line"
                    style={
                      isBareHex(colors[field.body].trim())
                        ? { background: `#${colors[field.body].trim()}` }
                        : undefined
                    }
                  />
                </div>
              )}
            </Field>
          ))}
        </div>

        <p className="mt-3 text-[length:var(--text-xs)] text-muted" data-testid="semantic-note">
          The danger, warning and success colours are platform-defined and deliberately not
          editable: a destructive action has to look destructive in every brand (§11.6), so the API
          rejects them by name.
        </p>
      </section>

      <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
        <h2 className="text-[length:var(--text-lg)] font-medium text-body">Type and shape</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Product name" error={fieldErrors['productName']}>
            {(props) => (
              <Input
                {...props}
                value={productName}
                disabled={!canWrite}
                maxLength={64}
                onChange={(e) => setProductName(e.target.value)}
              />
            )}
          </Field>

          <Field label="Font" error={fieldErrors['fontFamily']}>
            {(props) => (
              <Select
                {...props}
                value={fontFamily}
                disabled={!canWrite}
                onChange={(e) => setFontFamily(e.target.value)}
              >
                {/* The server validates against this same list (§11.3) — a
                    family name is never a URL. */}
                {ALLOWED_FONT_FAMILIES.map((family) => (
                  <option key={family} value={family}>
                    {family}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Corner radius" error={fieldErrors['radiusScale']}>
            {(props) => (
              <Select
                {...props}
                value={radiusScale}
                disabled={!canWrite}
                onChange={(e) => setRadiusScale(e.target.value as RadiusScale)}
              >
                {RADIUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </section>

      <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
        <h2 className="text-[length:var(--text-lg)] font-medium text-body">Footer and support</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field
            label="Support email"
            hint="Shown where users are told to ask for help. Clear it to show none."
            error={fieldErrors['supportEmail']}
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                value={supportEmail}
                disabled={!canWrite}
                onChange={(e) => setSupportEmail(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Footer text"
            hint="A short line under every page. Clear it to show none."
            error={fieldErrors['footerText']}
          >
            {(props) => (
              <Input
                {...props}
                value={footerText}
                disabled={!canWrite}
                maxLength={200}
                onChange={(e) => setFooterText(e.target.value)}
              />
            )}
          </Field>
        </div>
      </section>

      {/*
        Non-blocking advisories from the server — a 3.0–4.5:1 colour saves,
        but carries its caution back in the payload. Swallowing these would
        make "it saved" read as "it is fine".
      */}
      {payload.warnings.length > 0 ? (
        <ul
          data-testid="branding-warnings"
          className="flex list-disc flex-col gap-1 rounded-[var(--radius-md)] border border-line bg-surface-alt p-4 pl-8 text-[length:var(--text-sm)] text-body"
        >
          {payload.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}

      {/* The summary line; field-level details already sit on their inputs. */}
      {update.isError && Object.keys(fieldErrors).length === 0 ? (
        <ErrorState error={update.error} />
      ) : null}

      {canWrite ? (
        <div className="flex gap-2">
          <Button type="submit" loading={update.isPending} disabled={!isDirty || update.isPending}>
            Save branding
          </Button>
        </div>
      ) : null}
    </form>
  );
}
